'use client';

import React from 'react';
import { Popover } from '@barges/react';
import type { SetupErrorInfo } from '../hooks/useSetupAction';
import { PURCHASE_FLOW_ICONS } from '../utils/constants';
import { usePurchaseRender } from '../config/PurchaseFlowConfigProvider';
import classes from './classes.module.scss';

interface ErrorPopupClassNames {
  popup?: string;
  container?: string;
  icon?: string;
  text?: string;
  okBtn?: string;
}

interface ErrorPopupProps {
  error: SetupErrorInfo | undefined;
  onDismiss: () => void;
  /** When provided, renders a back button that closes the flow entirely */
  onBack?: () => void;
  /** Optional icon rendered above the title */
  icon?: React.ReactNode;
  /** Custom OK button label (defaults to "OK") */
  okLabel?: string;
  /** Per-element class overrides for context-specific styling */
  classNames?: ErrorPopupClassNames;
  /** When true the popup is always centered; when false it uses the Popover
   *  default (centered on desktop, bottom-sheet on mobile). Defaults to false
   *  so the popup renders as a bottom-sheet on mobile, matching the old flow. */
  centered?: boolean;
}

const cx = (base: string, override?: string) => (override ? `${ base } ${ override }` : base);

const ErrorPopup: React.FC<ErrorPopupProps> = ({
  error, onDismiss, onBack, icon, okLabel = 'OK', classNames: cn, centered = false
}) => {
  const { renderIcon } = usePurchaseRender();

  return (
    <Popover isOpen={ !!error } centered={ centered } className={ cx(classes.errorPopup, cn?.popup) } data-testid="purchase-flow-error-popup">
      <div className={ cx(classes.errorPopupContainer, cn?.container) }>
        {onBack && (
          <div className={ classes.errorPopupBackRow }>
            <button type="button" onClick={ onBack } aria-label="Back" className={ classes.headerButton } data-testid="purchase-flow-error-back-button">
              {renderIcon(PURCHASE_FLOW_ICONS.BACK)}
            </button>
          </div>
        )}
        {icon && <div className={ cx(classes.errorPopupIcon, cn?.icon) }>{icon}</div>}
        {error?.title && <div className={ classes.errorPopupTitle } data-testid="purchase-flow-error-title">{error.title}</div>}
        {error?.message && <div className={ cx(classes.errorPopupText, cn?.text) } data-testid="purchase-flow-error-message">{error.message}</div>}
        <button type="button" onClick={ onDismiss } className={ cx(classes.errorPopupOkBtn, cn?.okBtn) } data-testid="purchase-flow-error-ok-button">{okLabel}</button>
      </div>
    </Popover>
  );
};

export default ErrorPopup;
